import React, { useEffect } from 'react';
import { css } from '@emotion/react';
import {
	QUERY_REQUEST_DATA,
	QUERY_REQUEST_STATE,
	MUTATION_UPDATE_DATA_PROMOTER,
} from 'queries/queries-booking';
import { Controller, useForm } from 'react-hook-form';

import { useMutation, useQuery } from 'urql';
import { Button } from '../../../components/Button';
import { FormControl } from '../../../components/FormControl';
import { FormFooter } from '../../../components/FormFooter';
import { FormLine } from '../../../components/FormLine';
import { FormSection } from '../../../components/FormSection';

import { Heading } from '../../../components/Heading';
import { Input } from '../../../components/Input';
import {
	emailValidation,
	icoValidation,
	nonEmptyValidation,
	phoneValidation,
	zipValidation,
} from '../forms/validations';
import { useStep } from '../hooks/useStep';
import { confirmedRedirect } from '../helpers/helpers';
import { useNavigate } from 'react-router-dom';
import { IconEmail } from 'icons/icon-email';
import { IconPhone } from 'icons/icon-phone';

import { SpinnerBlock } from 'components/SpinnerBlock';
import { useStore } from 'hooks/useStore';
import { FormSectionHeading } from 'components/FormSectionHeading';
import { PhoneInput } from 'components/PhoneInput';
import { useRequestStateQuery } from 'generated/graphql';

enum FORM_PROMOTER {
	NAME = 'promoter_name',
	EMAIL = 'promoter_email',
	PHONE = 'promoter_phone',

	CONTRACT_ICO = 'contract_ico',
	CONTRACT_NAME = 'contract_name',
	CONTRACT_ADDRESS_STREET = 'contract_address_street',
	CONTRACT_ADDRESS_CITY = 'contract_address_city',
	CONTRACT_ADDRESS_ZIP = 'contract_address_zip',
}

export const PromoterStep = () => {
	const { id } = useStep();
	const navigate = useNavigate();

	const [queryRequestData] = useQuery({
		query: QUERY_REQUEST_DATA,
		variables: {
			url: id,
		},
	});

	const [_stateRequest] = useRequestStateQuery({
		variables: { url: id },
	});

	useEffect(() => {
		if (!queryRequestData.fetching) {
			confirmedRedirect(id, queryRequestData.data.request.confirmed, navigate);
		}
	}, [queryRequestData.data]);

	return (
		<div
			css={css`
				width: 560px;
			`}
		>
			<Heading title="Promotér" />

			{queryRequestData.fetching ? (
				<SpinnerBlock />
			) : (
				<Form defaultValues={queryRequestData?.data?.request} url={id} />
			)}
		</div>
	);
};

const Form = ({ defaultValues, url }) => {
	const { notifications } = useStore();

	const { goNextStep, goPrevStep } = useStep();

	const localDefaultValues = {
		promoter_name: defaultValues?.promoter_name,
		promoter_email: defaultValues?.promoter_email,
		promoter_phone: defaultValues?.promoter_phone,

		contract_ico: defaultValues?.contract_ico,
		contract_name: defaultValues?.contract_name,
		contract_address_street: defaultValues?.contract_address_street,
		contract_address_city: defaultValues?.contract_address_city,
		contract_address_zip: defaultValues?.contract_address_zip,
	};

	const {
		handleSubmit,
		register,
		control,
		formState: { errors },
	} = useForm({ defaultValues: localDefaultValues });

	// Submit Form
	const [updateDataPromoter, executeUpdateDataPromoter] = useMutation(
		MUTATION_UPDATE_DATA_PROMOTER,
	);

	const onSubmit = (data: any) => {
		// console.log('SUBMIT');
		// console.log(data);

		executeUpdateDataPromoter({
			url: url,
			input: data,
		}).then((result) => {
			// console.log(result);

			if (result?.error) {
				// VYHODIT ERROR
				return false;
			} else {
				notifications.addNotification({ message: 'Uloženo' });
			}

			goNextStep();
		});
	};

	const labelWidth = 178;

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<FormSection>
				<FormLine first>
					<FormControl
						required
						label="Jméno"
						htmlFor={FORM_PROMOTER.NAME}
						labelWidth={labelWidth}
						error={errors[FORM_PROMOTER.NAME]}
					>
						<Input
							placeholder="Zadejte celé jméno..."
							{...register(
								FORM_PROMOTER.NAME,
								nonEmptyValidation({ required: 'Zadejte celé jméno promotéra.' }),
							)}
							error={!!errors[FORM_PROMOTER.NAME]}
						/>
					</FormControl>
				</FormLine>

				<FormLine>
					<FormControl
						required
						label="E-mail"
						htmlFor={FORM_PROMOTER.EMAIL}
						labelWidth={labelWidth}
						error={errors[FORM_PROMOTER.EMAIL]}
					>
						<Input
							icon={<IconEmail />}
							placeholder=""
							{...register(
								FORM_PROMOTER.EMAIL,
								emailValidation({ required: 'Zadejte e-mail promotéra.' }),
							)}
							error={!!errors[FORM_PROMOTER.EMAIL]}
						/>
					</FormControl>
				</FormLine>

				<FormLine>
					<FormControl
						required
						label="Telefonní číslo"
						htmlFor={FORM_PROMOTER.PHONE}
						labelWidth={labelWidth}
						error={errors[FORM_PROMOTER.PHONE]}
					>
						<Controller
							name={FORM_PROMOTER.PHONE}
							control={control}
							rules={{
								...phoneValidation({ required: true }),
							}}
							render={({ field }) => {
								return <PhoneInput {...field} width={240} error={!!errors[FORM_PROMOTER.PHONE]} />;
							}}
						/>
					</FormControl>
				</FormLine>

				<FormSectionHeading>Smlouva</FormSectionHeading>

				<FormLine first>
					<FormControl
						required
						label="IČO pro účely smlouvy"
						htmlFor={FORM_PROMOTER.CONTRACT_ICO}
						labelWidth={labelWidth}
						error={errors[FORM_PROMOTER.CONTRACT_ICO]}
					>
						<Input
							placeholder="8-místné číslo..."
							{...register(FORM_PROMOTER.CONTRACT_ICO, icoValidation())}
							error={!!errors[FORM_PROMOTER.CONTRACT_ICO]}
						/>
					</FormControl>
				</FormLine>

				<FormLine>
					<FormControl
						required
						label="Název firmy"
						htmlFor={FORM_PROMOTER.CONTRACT_NAME}
						labelWidth={labelWidth}
						error={errors[FORM_PROMOTER.CONTRACT_NAME]}
					>
						<Input
							placeholder="Zadejte název firmy..."
							{...register(
								FORM_PROMOTER.CONTRACT_NAME,
								nonEmptyValidation({ required: 'Zadejte název firmy.' }),
							)}
							error={!!errors[FORM_PROMOTER.CONTRACT_NAME]}
						/>
					</FormControl>
				</FormLine>

				<FormLine>
					<FormControl
						required
						label="Ulice"
						htmlFor={FORM_PROMOTER.CONTRACT_ADDRESS_STREET}
						labelWidth={labelWidth}
						error={errors[FORM_PROMOTER.CONTRACT_ADDRESS_STREET]}
					>
						<Input
							placeholder=""
							{...register(
								FORM_PROMOTER.CONTRACT_ADDRESS_STREET,
								nonEmptyValidation({ required: 'Zadejte ulici.' }),
							)}
							error={!!errors[FORM_PROMOTER.CONTRACT_ADDRESS_STREET]}
						/>
					</FormControl>
				</FormLine>

				<FormLine>
					<FormControl
						required
						label="Město"
						htmlFor={FORM_PROMOTER.CONTRACT_ADDRESS_CITY}
						labelWidth={labelWidth}
						error={errors[FORM_PROMOTER.CONTRACT_ADDRESS_CITY]}
					>
						<Input
							placeholder=""
							{...register(
								FORM_PROMOTER.CONTRACT_ADDRESS_CITY,
								nonEmptyValidation({ required: 'Zadejte město.' }),
							)}
							error={!!errors[FORM_PROMOTER.CONTRACT_ADDRESS_CITY]}
						/>
					</FormControl>
				</FormLine>

				<FormLine>
					<FormControl
						required
						label="PSČ"
						htmlFor={FORM_PROMOTER.CONTRACT_ADDRESS_ZIP}
						labelWidth={labelWidth}
						error={errors[FORM_PROMOTER.CONTRACT_ADDRESS_ZIP]}
					>
						<Input
							{...register(
								FORM_PROMOTER.CONTRACT_ADDRESS_ZIP,

								zipValidation({
									required: 'Zadejte PSČ.',
								}),
							)}
							error={!!errors[FORM_PROMOTER.CONTRACT_ADDRESS_ZIP]}
							width={80}
						/>
					</FormControl>
				</FormLine>
			</FormSection>

			<FormFooter>
				<Button outline type="button" onClick={() => goPrevStep()}>
					Zpět
				</Button>
				<Button type="submit" isProcessing={updateDataPromoter.fetching}>
					Uložit a pokračovat
				</Button>
			</FormFooter>
		</form>
	);
};
